import { usePerms } from 'contexts/PermsContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useContext, useEffect, useId, useMemo, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaHouse } from 'react-icons/fa6';
import { PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { Link, useLocation } from 'react-router-dom';
import { getLogo } from 'utils/images/image.utils';
import './assets/nav.scss';
import {
  buildSidebarData,
  findOpenDropdownName,
  getEffectiveSelectedPath,
  isDropdownSelected,
} from './navStateUtils';

/** Shows permitted destinations and keeps the current page's section expanded. */
export default function Sidebar({ onOpenChanged, updateSidebar }) {
  const location = useLocation();
  const menuId = useId();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const { isAuthenticated } = useContext(AuthContext);
  const perms = usePerms();

  const selectedPage = getEffectiveSelectedPath(location);
  const pageButtons = useMemo(
    () => (isAuthenticated ? buildSidebarData(perms) : []),
    [isAuthenticated, perms, updateSidebar],
  );
  const publicInfoIndex = pageButtons.findIndex((page) => page.name === 'Public Info');
  const primaryPageButtons =
    publicInfoIndex === -1 ? pageButtons : pageButtons.slice(0, publicInfoIndex + 1);
  const secondaryPageButtons = publicInfoIndex === -1 ? [] : pageButtons.slice(publicInfoIndex + 1);

  useEffect(() => {
    if (!isAuthenticated) {
      setOpenDropdownName(null);
      return;
    }

    setOpenDropdownName(findOpenDropdownName(pageButtons, selectedPage));
  }, [isAuthenticated, pageButtons, selectedPage]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
  }, [isAuthenticated]);

  const toggleSidebar = () => {
    const nextSidebarOpen = !sidebarOpen;
    if (!nextSidebarOpen) setOpenDropdownName(null);

    onOpenChanged(nextSidebarOpen);
    setSidebarOpen(nextSidebarOpen);
  };

  const closeAllDropdowns = () => {
    setOpenDropdownName(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (!sidebarOpen) {
      onOpenChanged(true);
      setSidebarOpen(true);
      setOpenDropdownName(dropdownName);
      return;
    }

    setOpenDropdownName((currentDropdownName) =>
      currentDropdownName === dropdownName ? null : dropdownName,
    );
  };

  const renderDropdown = (dropdown, index) => {
    if (!dropdown.enabled) return null;

    const dropdownSelected = isDropdownSelected(dropdown, selectedPage);
    const dropdownOpen = sidebarOpen && openDropdownName === dropdown.name;
    const submenuId = `${menuId}-${index}`;

    return (
      <div key={dropdown.name} className="navigation-group">
        <button
          type="button"
          className="navigation-item"
          data-selected={dropdownSelected}
          data-open={dropdownOpen}
          onClick={() => toggleDropdown(dropdown.name)}
          aria-label={dropdown.name}
          aria-expanded={dropdownOpen}
          aria-controls={dropdownOpen ? submenuId : undefined}
          title={sidebarOpen ? undefined : dropdown.name}
        >
          <span className="navigation-icon" aria-hidden="true">
            {dropdown.icon()}
          </span>
          {sidebarOpen && (
            <>
              <span className="navigation-label">{dropdown.name}</span>
              <FaChevronDown className="navigation-chevron" aria-hidden="true" />
            </>
          )}
        </button>
        <Collapse in={dropdownOpen} mountOnEnter unmountOnExit>
          <div id={submenuId} className="navigation-submenu-list">
            {dropdown.options.map((option) => {
              if (!option.enabled) return null;

              return (
                <Link
                  key={option.href}
                  className="navigation-item navigation-submenu-item"
                  to={option.href}
                  aria-current={selectedPage === option.href ? 'page' : undefined}
                >
                  <span className="navigation-icon" aria-hidden="true">
                    {option.icon()}
                  </span>
                  <span className="navigation-label">{option.name}</span>
                </Link>
              );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  if (!isAuthenticated) return null;

  return (
    <nav
      aria-label="Main navigation"
      className="fusion-sidebar-shell h-100 overflow-y-auto overflow-x-hidden customScrollbar-Y navigation-menu"
      data-collapsed={!sidebarOpen}
      style={{ width: sidebarOpen ? 300 : 65 }}
    >
      <div className="navigation-brand-area">
        <Link
          to="/dashboard"
          className="navigation-brand-link"
          aria-label="Go to dashboard"
          onClick={closeAllDropdowns}
        >
          <img className="navigation-logo" src={getLogo(sidebarOpen)} alt="Logo" />
        </Link>
        <button
          type="button"
          className="navigation-toggle"
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={sidebarOpen}
          title={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
        >
          {sidebarOpen ? (
            <FaChevronLeft aria-hidden="true" />
          ) : (
            <FaChevronRight aria-hidden="true" />
          )}
        </button>
      </div>
      <div className="navigation-content">
        <Link
          className="navigation-item"
          to="/dashboard"
          onClick={closeAllDropdowns}
          aria-label="Dashboard"
          aria-current={selectedPage === '/dashboard' ? 'page' : undefined}
          title={sidebarOpen ? undefined : 'Dashboard'}
        >
          <span className="navigation-icon" aria-hidden="true">
            <FaHouse />
          </span>
          {sidebarOpen && <span className="navigation-label">Dashboard</span>}
        </Link>

        {primaryPageButtons.map((dropdown, index) => renderDropdown(dropdown, index))}

        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewExacqVisionOverwatchVMSConnections'],
        ) && (
          <Link
            className="navigation-item"
            to="/vms"
            onClick={closeAllDropdowns}
            aria-label="Overwatch VMS"
            aria-current={selectedPage === '/vms' ? 'page' : undefined}
            title={sidebarOpen ? undefined : 'Overwatch VMS'}
          >
            <span className="navigation-icon" aria-hidden="true">
              <PiVideoFill />
            </span>
            {sidebarOpen && <span className="navigation-label">Overwatch VMS</span>}
          </Link>
        )}

        {perms.maintenanceLevel > 0 && (
          <Link
            className="navigation-item"
            to="/maintenancedashboard"
            onClick={closeAllDropdowns}
            aria-label="Maintenance Devices"
            aria-current={selectedPage === '/maintenancedashboard' ? 'page' : undefined}
            title={sidebarOpen ? undefined : 'Maintenance Devices'}
          >
            <span className="navigation-icon" aria-hidden="true">
              <PiWrenchFill />
            </span>
            {sidebarOpen && <span className="navigation-label">Maintenance Devices</span>}
          </Link>
        )}

        {secondaryPageButtons.map((dropdown, index) =>
          renderDropdown(dropdown, primaryPageButtons.length + index),
        )}
      </div>
    </nav>
  );
}
